import { CheckIcon } from '@heroicons/react/solid';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import { Price, ProductWithPrice } from 'types';

import clsxm from '@/lib/clsxm';

import { STARTER_PROMO } from '@/constants/common';
import { getReferralBonusForPlan } from '@/constants/referrals';

import Button from './buttons/Button';
import LogosCloudPricing from './LogosCloudPricing';

interface Props {
  products: ProductWithPrice[];
  isUpgrade?: boolean;
  hideLowerPrices?: boolean;
  message?: string;
  showOnlyTTSFeatures?: boolean;
  darkBackground?: boolean;
  // When the visitor arrived via a referral link, show the bonus each plan
  // unlocks directly on its card.
  showReferralBonus?: boolean;
}

type BillingInterval = 'year' | 'month';

export default function PricingForLandingPage({
  products,
  isUpgrade = false,
  hideLowerPrices = true,
  message = '100% no risk, 30-day money back guarantee.',
  showOnlyTTSFeatures = false,
  darkBackground = false,
  showReferralBonus = false,
}: Props) {
  const router = useRouter();
  const { t } = useLocale('pricing');
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('year');
  const [priceIdLoading, setPriceIdLoading] = useState<string>();
  const user = false;

  const isStarterMonthlyPromo = (product: ProductWithPrice, price: Price) =>
    product.id === STARTER_PROMO.productId &&
    price.interval === STARTER_PROMO.billingInterval;

  const handleCheckout = async (price: Price) => {
    setPriceIdLoading(price.id);
    if (!user) {
      return router.push(`/login?price=${price.id}#auth-sign-up`);
    }
  };

  const getYearlySavings = (productName: string): string => {
    const product = products.find((p) => p.name === productName);
    const monthlyPrice = product?.prices?.find(
      (price) => price.interval === 'month',
    );

    if (monthlyPrice?.unit_amount) {
      const yearlySavings = (monthlyPrice.unit_amount / 100) * 12;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: monthlyPrice.currency as string,
        minimumFractionDigits: 0,
      }).format(yearlySavings);
    }

    return '$0';
  };

  if (!products.length) return <h1>No items</h1>;

  return (
    <section
      id='pricing'
      className={`${clsxm(isUpgrade ? 'rounded-md' : '', darkBackground ? 'bg-gray-900' : 'bg-transparent', 'py-24')}`}
    >
      <div className='mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-24'>
        <div className='sm:align-center sm:flex sm:flex-col'>
          <h2 className='text-4xl font-extrabold text-white sm:text-center sm:text-6xl'>
            {t('Pricing Plans')}
          </h2>
          {!isUpgrade ? (
            <p className='m-auto mt-5 max-w-2xl text-xl text-zinc-200 sm:text-center sm:text-2xl'>
              <span className=' font-extrabold'>Limited-time Promotion</span>.
              Start a 7-day free trial
            </p>
          ) : (
            <p className='m-auto mt-5 max-w-2xl text-xl text-zinc-200 sm:text-center sm:text-2xl'>
              {message && <span className=' font-extrabold'>{message}</span>}
            </p>
          )}

          <div className='relative mt-8 flex self-center rounded-full bg-white/5 p-1 sm:mt-10 border border-white/10'>
            <button
              onClick={() => setBillingInterval('month')}
              type='button'
              className={`${
                billingInterval === 'month'
                  ? 'bg-white font-bold text-gray-900 shadow-lg'
                  : 'bg-transparent font-medium text-gray-300 hover:text-white'
              } relative w-1/2 rounded-full py-3 text-lg transition-all duration-200 focus:outline-none sm:w-auto sm:px-10`}
            >
              {t('Monthly billing')}
            </button>
            <button
              onClick={() => setBillingInterval('year')}
              type='button'
              className={`${
                billingInterval === 'year'
                  ? 'bg-white font-bold text-gray-900 shadow-lg'
                  : 'bg-transparent font-medium text-gray-300 hover:text-white'
              } relative w-1/2 rounded-full py-3 text-lg transition-all duration-200 focus:outline-none sm:w-auto sm:px-10`}
            >
              {t('Yearly billing')}
              <span className='absolute -right-4 -top-4 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-r from-green-400 to-emerald-500 p-5 text-xs font-bold text-white shadow-lg'>
                -50%
              </span>
            </button>
          </div>
        </div>
        {/* <div className='flex items-center justify-center'>
          <div className='mt-10 flex w-fit rounded-full bg-pink-600 px-4 py-1.5 text-center text-sm font-semibold text-white'>
            💎 {t('Pay annually, get 2 months free')}
          </div>
        </div> */}
        <div
          className={clsxm(
            products.length > 3
              ? 'xl:mx-0 xl:max-w-none xl:grid-cols-4'
              : products.length > 2
                ? 'xl:grid-cols-3'
                : 'xl:grid-cols-1',
            'mt-12 space-y-4 sm:mt-16 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:mx-auto lg:max-w-4xl  ',
          )}
        >
          {products
            .filter((product) => {
              const price = product?.prices?.find(
                (price) => price.interval === billingInterval,
              );
              return true;
            })
            .map((product) => {
              const price = product?.prices?.find(
                (price) => price.interval === billingInterval,
              );
              if (!price) return null;
              const priceString = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: price.currency as string,
                minimumFractionDigits: 0,
              }).format((price?.unit_amount || 0) / 100);
              const hasPromo = isStarterMonthlyPromo(product, price as Price);
              const referralBonus = showReferralBonus
                ? getReferralBonusForPlan(product.name)
                : null;
              const promoPrice = hasPromo
                ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: price.currency as string,
                    minimumFractionDigits: 0,
                  }).format(
                    ((price?.unit_amount || 0) / 100) *
                      (1 - STARTER_PROMO.discountPercent / 100),
                  )
                : null;

              return (
                <div
                  key={product.id}
                  className={clsxm(
                    'relative mx-auto max-w-sm rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl transition-all hover:-translate-y-1 hover:border-white/20',
                    product.name === 'Unlimited' &&
                      'border-purple-500/50 bg-gradient-to-b from-purple-900/20 to-white/5 ring-1 ring-purple-500/20',
                    hasPromo && 'border-green-500/50 ring-1 ring-green-500/30',
                  )}
                >
                  <div className='p-5'>
                    <h3 className='text-2xl font-semibold leading-6 text-white'>
                      {product.name}
                    </h3>
                    {product.name === 'Unlimited' && (
                      <p className='absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-1 text-sm font-bold text-white shadow-lg'>
                        {t('Most popular')}
                      </p>
                    )}
                    {hasPromo && (
                      <p className='absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 transform whitespace-nowrap rounded-full bg-green-500 px-4 py-1 text-sm font-bold text-white shadow-lg'>
                        {STARTER_PROMO.discountPercent}% OFF First Month
                      </p>
                    )}
                    <p className='mt-4 text-zinc-300'>{product.description}</p>
                    {referralBonus && (
                      <p className='mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-300'>
                        🎁 {t('Referral bonus')}: +
                        {referralBonus.words.toLocaleString()}{' '}
                        {t('bonus words')} + {referralBonus.images}{' '}
                        {t('image credits')}
                      </p>
                    )}
                    <div className='mt-8'>
                      <div>
                        {billingInterval === 'year' && (
                          <div>
                            <span className='white text-4xl font-extrabold text-white'>
                              $
                              {Math.round(
                                ((price?.unit_amount || 0) / 100 / 12) * 100,
                              ) / 100}
                            </span>
                            <span className='text-base font-medium text-zinc-100'>
                              /{t('month')}
                            </span>
                            <span className='block text-base  font-medium text-rose-500'>
                              <span className='mr-1 text-white'>
                                {t('billed yearly')}
                              </span>
                              <span className='text-gray-300 line-through'>
                                {getYearlySavings(product.name as string)}
                              </span>{' '}
                              {priceString}
                            </span>
                          </div>
                        )}{' '}
                        {billingInterval === 'month' && (
                          <div>
                            {hasPromo ? (
                              <>
                                <span className='white text-4xl font-extrabold text-white'>
                                  {promoPrice}
                                </span>
                                <span className='text-base font-medium text-zinc-100'>
                                  /{t(billingInterval)}
                                </span>
                                <span className='ml-2 text-lg text-gray-400 line-through'>
                                  {priceString}
                                </span>
                                <span className='mt-1 block text-sm font-semibold text-green-400'>
                                  first month with code {STARTER_PROMO.code}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className='white text-4xl font-extrabold text-white'>
                                  {priceString}
                                </span>
                                <span className='text-base font-medium text-zinc-100'>
                                  /{t(billingInterval)}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant='primary'
                      type='button'
                      disabled={false}
                      isLoading={priceIdLoading === price.id}
                      onClick={() => handleCheckout(price as Price)}
                      className={clsxm(
                        'mt-8 block w-full rounded-full py-3 text-center text-sm font-bold transition-all',
                        hasPromo
                          ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg hover:shadow-green-500/25'
                          : product.name === 'Unlimited'
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg hover:shadow-purple-500/25'
                            : 'bg-white text-gray-900 hover:bg-gray-100 hover:text-gray-900',
                      )}
                    >
                      <span>{t('Start')}</span>
                    </Button>
                    {billingInterval === 'month' && (
                      <p
                        className='mt-2 cursor-pointer text-center text-sm text-green-500 hover:underline'
                        onClick={() => setBillingInterval('year')}
                      >
                        <span>{t('Save 40% with yearly billing')} ↗</span>
                      </p>
                    )}{' '}
                    {billingInterval === 'year' && (
                      <p
                        className='mt-2 cursor-pointer text-center text-sm text-zinc-400 hover:underline'
                        onClick={() => setBillingInterval('month')}
                      >
                        {t('View monthly billing')} ↗
                      </p>
                    )}
                  </div>
                  {Array.isArray(
                    // @ts-ignore
                    product.metadata?.includedFeatures,
                  ) && (
                    <div className='px-6 pb-8'>
                      {/* <span className='text-sm font-medium text-white'>
                        {t("What's included")}
                      </span> */}
                      <ul role='list' className='mt-6 space-y-4'>
                        {
                          // @ts-ignore
                          (product.metadata.includedFeatures as [])
                            .filter((feature) => {
                              if (showOnlyTTSFeatures) {
                                // @ts-ignore
                                return feature.includes('Speech');
                              }
                              return true;
                            })
                            .map((feature) => (
                              <li key={feature} className='flex space-x-3'>
                                <CheckIcon
                                  className='h-5 w-5 flex-shrink-0 text-green-500'
                                  aria-hidden='true'
                                />
                                <span className='text-sm text-white'>
                                  {t(feature)}
                                </span>
                              </li>
                            ))
                        }
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        <p className='m-auto mt-5 max-w-4xl text-sm text-zinc-300 sm:text-center'>
          *{' '}
          {t(
            'The models Claude 3 Haiku, GPT-4o mini, GPT-3.5, Llama 3 70B, and Mixtral 8x7b offer unlimited usage.',
          )}
          <br />
          {t(
            'The GPT-4o/Claude 3.5 Sonnet/Google Gemini models provide twice as many words as the GPT-4/Claude 3 Opus/Mixtral Large models.',
          )}
          <br />
          {t('Don’t see a plan that meets your needs?')}{' '}
          <Link href='/contact' target='_blank' className='underline'>
            {t('Contact our sales team')}
          </Link>{' '}
          {t('and we’ll create a custom plan just for you.')}
        </p>
        <p className='m-auto mt-10 max-w-2xl text-xl text-zinc-200 sm:text-center sm:text-2xl'>
          {t('Join 100,000+ creators, brands & publishers')}
        </p>
        <LogosCloudPricing hideManyMode={true} />
        <p className='m-auto mt-10 max-w-2xl text-xl text-zinc-200 sm:text-center sm:text-2xl'>
          🔒 {t('Payments are securely processed by Stripe.')}
        </p>
        {/* <p className='m-auto mt-5 max-w-2xl text-lg text-zinc-200 sm:text-center'>
          🌏{' '}
          {t(
            'Easy-Peasy.AI contributes 1% of purchases to remove CO₂ from the atmosphere.'
          )}
        </p> */}
      </div>
    </section>
  );
}
